import { palaceShort } from '../../colors'

/**
 * Двенадцать дворцов кольцом. Кольцо, а не сетка 4×4 из комнаты Цзы Вэй:
 * здесь важен не адрес по ветвям, а «какой сектор жизни сейчас подсвечен» —
 * дворец Судьбы, Тела, десятилетия и года.
 *
 * Названия дворцов приходят из /api/systems; если их нет, кольцо всё равно
 * рисуется — подписан остаётся только выделенный сектор.
 */
export interface PalaceRingHighlight {
  life?: string | null
  body?: string | null
  decade?: string | null
  annual?: string | null
}

export interface PalaceRingItem {
  id: string
  titleRu?: string
}

const WIDTH = 340
const HEIGHT = 300
const CENTER_X = 170
const CENTER_Y = 148
const OUTER = 84
const INNER = 62
const LABEL_ORBIT = 102

const ROLE_COLORS: { key: keyof PalaceRingHighlight; label: string; color: string }[] = [
  { key: 'life', label: 'Судьба', color: 'var(--gold)' },
  { key: 'body', label: 'Тело', color: 'var(--turq)' },
  { key: 'decade', label: 'Десятилетие', color: 'var(--ochre)' },
  { key: 'annual', label: 'Год', color: 'var(--bordo)' },
]

function polar(radius: number, degrees: number): { x: number; y: number } {
  const angle = ((degrees - 90) * Math.PI) / 180
  return { x: CENTER_X + radius * Math.cos(angle), y: CENTER_Y + radius * Math.sin(angle) }
}

/** Сектор кольца между двумя углами: внешняя дуга туда, внутренняя обратно. */
function sectorPath(from: number, to: number): string {
  const outerStart = polar(OUTER, from)
  const outerEnd = polar(OUTER, to)
  const innerEnd = polar(INNER, to)
  const innerStart = polar(INNER, from)
  return [
    `M ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
    `A ${OUTER} ${OUTER} 0 0 1 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
    `L ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
    `A ${INNER} ${INNER} 0 0 0 ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
    'Z',
  ].join(' ')
}

export function PalaceRing({
  palaces,
  highlight = {},
}: {
  palaces: PalaceRingItem[]
  highlight?: PalaceRingHighlight
}) {
  if (palaces.length === 0) return null
  const span = 360 / palaces.length
  const roleOf = (id: string): { label: string; color: string } | null => {
    const role = ROLE_COLORS.find((item) => highlight[item.key] === id)
    return role ? { label: role.label, color: role.color } : null
  }
  const shown = ROLE_COLORS.filter((item) => {
    const value = highlight[item.key]
    return typeof value === 'string' && value.length > 0
  })

  return (
    <div className="viz-ring">
      <svg
        className="viz"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        role="img"
        aria-label="Двенадцать дворцов"
      >
        {palaces.map((palace, index) => {
          const from = index * span + 1
          const to = (index + 1) * span - 1
          const role = roleOf(palace.id)
          const middle = index * span + span / 2
          const label = palaceShort(palace.id, palace.titleRu ?? '')
          const point = polar(LABEL_ORBIT, middle)
          const cos = Math.cos(((middle - 90) * Math.PI) / 180)
          const anchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle'
          return (
            <g key={palace.id}>
              <path
                d={sectorPath(from, to)}
                style={{
                  fill: role ? role.color : 'var(--panel2)',
                  fillOpacity: role ? 0.28 : 1,
                  stroke: role ? role.color : 'var(--line2)',
                }}
                strokeWidth={1.5}
              />
              {label && (
                <text
                  x={point.x}
                  y={point.y + 4}
                  textAnchor={anchor}
                  className={`viz-cap${role ? ' on' : ''}`}
                  style={role ? { fill: role.color } : undefined}
                >
                  {label}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      {shown.length > 0 && (
        <div className="viz-legend">
          {shown.map((item) => (
            <span className="viz-legend-item" key={item.key}>
              <span className="viz-legend-dot" style={{ background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
